"use client";

import { useCallback, useEffect, useState } from "react";

// Invisible Cloudflare Turnstile for public-surface writes. The backend
// requires `x-turnstile-token` on posts/comments made with a forum session;
// this loads the script once, renders the widget into `#<containerId>` and
// hands back the token. No site key configured → no token (dev skips it).
export function useTurnstileToken(enabled: boolean, containerId: string) {
  const [token, setToken] = useState<string | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!enabled || !siteKey) return;
    const scriptId = "cf-turnstile-script";
    const render = () => {
      const w = window as any;
      const el = document.getElementById(containerId);
      if (!w.turnstile || !el || el.hasChildNodes()) return;
      w.turnstile.render(`#${containerId}`, {
        sitekey: siteKey,
        size: "invisible",
        callback: (t: string) => setToken(t),
        "expired-callback": () => setToken(null),
      });
    };
    if (document.getElementById(scriptId)) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [enabled, siteKey, containerId]);

  const reset = useCallback(() => {
    try {
      (window as any).turnstile?.reset?.();
    } catch {}
  }, []);

  const headers: Record<string, string> = token ? { "x-turnstile-token": token } : {};
  return { token, reset, headers };
}
